'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import moment from 'moment';
import 'moment/locale/uk';

import { Card, CardBody, CardHeader, Divider, Spacer } from '@heroui/react';
import { convertUnicode } from '@/libs/convertUnicode';
import { LikeButtonClient } from './likeButton/client';

moment.locale('uk');

export type ClientArticle = {
  id: string;
  title: string;
  image?: string | null;
  link: string;
  pubdate: string | Date;
  full_text?: string | null;
  reactedUsers: string[];
  sources: {
    id?: string;
    name?: string;
    logo?: string | null;
    target?: boolean | null;
  };
  categories: { id: string; name: string }[];
};

export const ArticleCardClient = ({ article, href, target }: { article: ClientArticle, href: string, target?: string }) => {
  const categories = article.categories || [];

  return (
    <>
      <Card
        isBlurred
        className="border-none bg-background/60 dark:bg-default-100/50"
        shadow="sm"
      >
        <CardHeader className="p-0 pl-3">
          <div className="flex gap-2 items-center w-full">
            <Image
              alt={article.sources?.name || 'Новини України'}
              height={24}
              width={24}
              loading="lazy"
              src={article.sources?.logo || "/logo.png"}
            />
            <div className="flex flex-col items-start">
              <p className="text-md">
                <span>
                  {article.sources?.id ? (
                    <Link href={`/source/${article.sources.id}`}>
                      {article.sources?.name}
                    </Link>
                  ) : (
                    article.sources?.name || ''
                  )}
                </span>
                <span className="text-default-500 pl-1">{moment(article.pubdate).fromNow()} в</span>
                <span className="pl-1">
                  {categories.length === 0 ? 'Інше' : null}
                  {categories.map((tag, index) => (
                    <React.Fragment key={tag.id}>
                      <Link href={`/category/${tag.id}`}>{tag.name}</Link>
                      {index !== categories.length - 1 ? <span className="text-default-500">, </span> : null}
                    </React.Fragment>
                  ))}
                </span>
              </p>
            </div>
            <div className="flex-grow" />
            <LikeButtonClient
              articleId={article.id}
              initialReactedUsers={article.reactedUsers}
            />
          </div>
        </CardHeader>
        <Divider />
        <CardBody>
          <div className="grid grid-cols-12 gap-6 pt-2">
            <div className="relative sm:col-span-3 col-span-12">
              <Link href={href} target={target} className="">
                <Image
                  alt={article.title}
                  width={280}
                  height={160}
                  loading="lazy"
                  src={article.image || "/no-image.png"}
                  className="article-image"
                />
              </Link>
            </div>
            <div className="flex flex-col sm:col-span-9 col-span-12">
              <Link href={href} target={target}>
                <h1 className="text-2xl font-medium" style={{ wordBreak: 'break-word' }}>{article.title}</h1>
              </Link>
            </div>
          </div>
          <Spacer y={2} />
          <div className="article-body article-body-short">
            {article.full_text ? convertUnicode(String(article.full_text).replace(/<[^>]*>?/gm, '')) : ''}
          </div>
        </CardBody>
      </Card>
      <Spacer y={4} />
    </>
  );
};
